import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toastServerError } from "helpers";
import { ApiError, ApiServiceError } from "libs/api";

import { AiAssistantRule } from "../../aiAgents.types";
import { AI_ASSISTANTS_QUERY_KEY } from "../../constants/enums";
import {
  CreateRuleBody,
  DeleteRuleBody,
  ReorderRulesBody,
  UpdateRuleBody,
  requestCreateRule,
  requestDeleteRule,
  requestGetRules,
  requestReorderRules,
  requestUpdateRule
} from "./rules.request";

const rulesKey = (assistantId: string) => [
  AI_ASSISTANTS_QUERY_KEY,
  "rules",
  assistantId
];

export const useGetRules = (assistantId: string | undefined) => {
  const query = useQuery<{ data: AiAssistantRule[] }, ApiServiceError<ApiError>>(
    {
      queryKey: rulesKey(assistantId ?? ""),
      queryFn: async () => await requestGetRules(assistantId!),
      enabled: Boolean(assistantId),
      onError: toastServerError
    }
  );

  return { rules: query.data?.data ?? [], ...query };
};

export const useRuleMutations = (assistantId: string) => {
  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: rulesKey(assistantId) });

  const createRule = useMutation<
    AiAssistantRule,
    ApiServiceError<ApiError>,
    Omit<CreateRuleBody, "assistantId">
  >({
    mutationFn: async (body) => await requestCreateRule({ assistantId, ...body }),
    onError: toastServerError,
    onSuccess: invalidate
  });

  const updateRule = useMutation<
    AiAssistantRule,
    ApiServiceError<ApiError>,
    Omit<UpdateRuleBody, "assistantId">
  >({
    mutationFn: async (body) => await requestUpdateRule({ assistantId, ...body }),
    onError: toastServerError,
    onSuccess: invalidate
  });

  const deleteRule = useMutation<
    { success: boolean },
    ApiServiceError<ApiError>,
    Omit<DeleteRuleBody, "assistantId">
  >({
    mutationFn: async (body) => await requestDeleteRule({ assistantId, ...body }),
    onError: toastServerError,
    onSuccess: invalidate
  });

  const reorderRules = useMutation<
    { data: AiAssistantRule[] },
    ApiServiceError<ApiError>,
    Omit<ReorderRulesBody, "assistantId">
  >({
    mutationFn: async (body) =>
      await requestReorderRules({ assistantId, ...body }),
    onError: toastServerError,
    onSuccess: invalidate
  });

  return { createRule, updateRule, deleteRule, reorderRules };
};
